import { AuthContextProvider } from "@elit-hair/elit-tools";
import { TooltipProvider } from "@elit-hair/elit-ui";
import { ErrorBoundary } from "@sentry/react";
import { RouterProvider } from "@tanstack/react-router";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";

import { CallFaviconIndicator } from "@/components/call-favicon-indicator";
import { GuidedTour } from "@/components/guided-tour/guided-tour";
import { GuidedTourProvider } from "@/components/guided-tour/guided-tour-context";
import { ENV } from "@/constants/env";
import { TelephonyServicesProvider } from "@/features/telephony";
import { RecordDetailsPanelProvider } from "@/pages/home/components/RecordDetailsPanelContext";
import { ClockProvider } from "@/providers/clock-provider";
import { LanguageProvider } from "@/providers/general/language-provider";
import { NotificationProviderWrapper } from "@/providers/general/notification-provider-wrapper";
import { UserStatusProvider } from "@/providers/general/user-status.provider";
import { CallProvider } from "@/providers/hooks/use-call";
import { PermissionsProvider } from "@/providers/hooks/use-permissions";
import { TopbarProvider } from "@/providers/hooks/use-topbar";
import { SingleInstanceProvider } from "@/providers/single-instance-provider";
import { handleAuthSvcLogout } from "@/utils/auth-token.utils";
import { isChunkLoadError, registerChunkLoadErrorRecovery } from "@/utils/chunk-load-error-recovery";

import { DarwinAI } from "./components/ai-chat";
import { ClarityIdentify } from "./components/clarity-provider";
import { DevIndicator } from "./components/dev-indicator";
import { LoadingScreen, SuspenseLoadingScreen } from "./components/loading-screen";
import { ConfettiProvider } from "./providers/confetti-provider";
import { SettingsProvider } from "./providers/general/settings-provider";
import { ThemeProviderWrapper } from "./providers/general/theme-provider-wrapper";
import { router } from "./router";
import "./styles/globals.css";
import "./styles/reactQuill.css";
import { QueryClientProvider } from "./utils/query-client";
import "./utils/sentry";

registerChunkLoadErrorRecovery();

function AppErrorFallback({ error }: { error: unknown }) {
    if (isChunkLoadError(error)) {
        window.location.reload();
        return <LoadingScreen />;
    }

    return <div>Something went wrong. Please try again later.</div>;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <ErrorBoundary fallback={({ error }) => <AppErrorFallback error={error} />}>
            <Suspense fallback={<SuspenseLoadingScreen />}>
                <SingleInstanceProvider>
                    <QueryClientProvider>
                        <TopbarProvider>
                            <AuthContextProvider beforeLogout={handleAuthSvcLogout}>
                                <ClarityIdentify projectId={ENV.CLARITY_PROJECT_ID} enabled={!ENV.DEV} />
                                <SettingsProvider>
                                    <ConfettiProvider>
                                        <ClockProvider>
                                            <ThemeProviderWrapper>
                                                <UserStatusProvider>
                                                    <PermissionsProvider>
                                                        <TelephonyServicesProvider>
                                                            <CallProvider>
                                                                <CallFaviconIndicator />
                                                                <LanguageProvider>
                                                                    <TooltipProvider>
                                                                        <RecordDetailsPanelProvider>
                                                                            <NotificationProviderWrapper>
                                                                                <GuidedTourProvider>
                                                                                    <RouterProvider router={router} />
                                                                                    <DarwinAI />
                                                                                    <GuidedTour />
                                                                                    <Toaster />
                                                                                    <DevIndicator />
                                                                                </GuidedTourProvider>
                                                                            </NotificationProviderWrapper>
                                                                        </RecordDetailsPanelProvider>
                                                                    </TooltipProvider>
                                                                </LanguageProvider>
                                                            </CallProvider>
                                                        </TelephonyServicesProvider>
                                                    </PermissionsProvider>
                                                </UserStatusProvider>
                                            </ThemeProviderWrapper>
                                        </ClockProvider>
                                    </ConfettiProvider>
                                </SettingsProvider>
                            </AuthContextProvider>
                        </TopbarProvider>
                    </QueryClientProvider>
                </SingleInstanceProvider>
            </Suspense>
        </ErrorBoundary>
    </React.StrictMode>
);
